import { OptionsGridModel, PackagePromo } from './types';

/**
 * Promo codes held per subject. A code fills the slot of every subject it covers and evicts
 * what was there. Keyed by subject, not package, so a code survives a duration switch.
 */

/** The code each subject holds, keyed by subjectsID. */
export type PromoCodeSlots = Record<number, string>;

/** A package as the quote and pricing responses describe one. */
type QuotePackage = {
  packagesID: number;
  subjectsID: number;
  promoCode?: string | null;
  /** The quote still names a code that has ended since it was made, but no longer applies it. */
  bPromoCodeExpired?: boolean;
  [key: string]: any;
};

const sameCode = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

const subjectIds = (slots: PromoCodeSlots): number[] =>
  Object.keys(slots).map((key) => Number(key));

/** The subjects a set of package ids belongs to, resolved through the quote's own packages. */
export const subjectsForPackageIds = (
  packageIds: number[],
  quotePackages: QuotePackage[] | null | undefined
): number[] => {
  const packages = quotePackages || [];

  return packageIds.reduce((subjects: number[], packagesId) => {
    const match = packages.find((pkg) => pkg && pkg.packagesID === packagesId);

    if (!match || subjects.indexOf(match.subjectsID) !== -1) {
      return subjects;
    }

    return [...subjects, match.subjectsID];
  }, []);
};

/** The grid's packages in the shape the slot helpers read, for a flow with no quote. */
export const packagesFromGrid = (grid: OptionsGridModel | null | undefined): QuotePackage[] =>
  ((grid && grid.subjects) || []).reduce(
    (packages: QuotePackage[], subject) => [
      ...packages,
      ...subject.cells.map((cell) => ({ packagesID: cell.packagesId, subjectsID: subject.subjectsId })),
    ],
    []
  );

/** Every covered subject takes the code, displacing whatever it held. */
export const applyCodeToSlots = (
  slots: PromoCodeSlots,
  promoCode: string,
  coveredSubjectIds: number[]
): PromoCodeSlots => {
  if (coveredSubjectIds.length === 0) {
    return slots;
  }

  return coveredSubjectIds.reduce(
    (next: PromoCodeSlots, subjectsId) => ({ ...next, [subjectsId]: promoCode }),
    { ...slots }
  );
};

/** The slots after clearing a code from every subject holding it. */
export const removeCodeFromSlots = (slots: PromoCodeSlots, promoCode: string): PromoCodeSlots =>
  subjectIds(slots).reduce((next: PromoCodeSlots, subjectsId) => {
    if (sameCode(slots[subjectsId], promoCode)) {
      return next;
    }

    return { ...next, [subjectsId]: slots[subjectsId] };
  }, {});

/** Every selected package carrying its subject's code; one without sends an empty one. */
export const codesForPricing = (
  packageIds: number[],
  slots: PromoCodeSlots,
  quotePackages: QuotePackage[] | null | undefined
): PackagePromo[] => {
  const packages = quotePackages || [];

  return packageIds.map((packagesId) => {
    const match = packages.find((pkg) => pkg && pkg.packagesID === packagesId);
    const promoCode = match ? slots[match.subjectsID] : undefined;

    return { packagesId, promoCode: promoCode || '' };
  });
};

/** The slots a quote arrives with; the quote already priced its codes per package. */
export const slotsFromQuotePackages = (
  quotePackages: QuotePackage[] | null | undefined
): PromoCodeSlots =>
  (quotePackages || []).reduce((slots: PromoCodeSlots, pkg) => {
    if (!pkg || typeof pkg.promoCode !== 'string' || pkg.promoCode.length === 0) {
      return slots;
    }

    if (pkg.bPromoCodeExpired || slots[pkg.subjectsID] !== undefined) {
      return slots;
    }

    return { ...slots, [pkg.subjectsID]: pkg.promoCode };
  }, {});

/** The codes a quote names that have ended since it was made, once each. */
export const expiredQuotePromoCodes = (
  quotePackages: QuotePackage[] | null | undefined
): string[] =>
  (quotePackages || []).reduce((expired: string[], pkg) => {
    if (!pkg || !pkg.bPromoCodeExpired || typeof pkg.promoCode !== 'string' || pkg.promoCode.length === 0) {
      return expired;
    }

    const code = pkg.promoCode;

    return expired.some((held) => sameCode(held, code)) ? expired : [...expired, code];
  }, []);

/** Codes no longer held anywhere after a change, so an eviction can be explained. */
export const droppedBySlotChange = (
  before: PromoCodeSlots,
  after: PromoCodeSlots
): string[] => {
  const remaining = subjectIds(after).map((subjectsId) => after[subjectsId]);

  return subjectIds(before).reduce((dropped: string[], subjectsId) => {
    const code = before[subjectsId];

    if (remaining.some((held) => sameCode(held, code))) {
      return dropped;
    }

    return dropped.some((held) => sameCode(held, code)) ? dropped : [...dropped, code];
  }, []);
};
